import {
  type FilterValue,
  type Filter,
} from "@shopify/hydrogen-react/storefront-api-types";

import { type DropdownOption } from "@react/components/Dropdown/Dropdown.data";
import {
  COLLECTION_FILTER_IDS,
  COLLECTION_PARAMS,
} from "@react/sections/Collection/CollectionFilters/constants";
import { currencySymbol } from "@root/tablescape/utilities/currency";
import { type SortOption } from "@tablescape/state";

import { transformToParamKey } from "./general";

const isCollectionParamKey = (key: string): key is COLLECTION_PARAMS => {
  return (Object.values(COLLECTION_PARAMS) as string[]).includes(key);
};

export const isCollectionFilterId = (
  key: string
): key is COLLECTION_FILTER_IDS => {
  return (Object.values(COLLECTION_FILTER_IDS) as string[]).includes(key);
};

export const isSortOption = (option: DropdownOption): option is SortOption => {
  if (!option || typeof option.value !== "object" || option.value === null) {
    return false;
  }
  const value = option.value;
  return (
    "reverse" in value &&
    typeof value.reverse === "boolean" &&
    "sortKey" in value
  );
};

const parsePriceFilter = (queryValue: string): FilterValue | null => {
  const match = queryValue.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (!match) return null;

  const min = parseFloat(match[1]);
  const max = parseFloat(match[2]);

  if (
    isNaN(min) ||
    isNaN(max) ||
    min < 0 ||
    max < 0 ||
    max > 1000000 ||
    min >= max
  )
    return null;

  return {
    id: COLLECTION_FILTER_IDS.PRICE,
    count: 0,
    input: JSON.stringify({ price: { min, max } }),
    label: `${currencySymbol()}${min} - ${currencySymbol()}${max}`,
  };
};

export const getAppliedFiltersFromQuery = (
  availableFilters: Filter[] | undefined
): FilterValue[] => {
  if (!availableFilters || availableFilters.length === 0) return [];

  if (typeof window === "undefined") return [];
  const params = new URLSearchParams(window.location.search);
  const preservedFilters: FilterValue[] = [];

  for (const [queryKey, queryValue] of params.entries()) {
    if (queryKey === COLLECTION_PARAMS.PRICE) {
      const priceFilter = parsePriceFilter(queryValue);
      if (priceFilter) preservedFilters.push(priceFilter);
      continue;
    }

    const filterMatch = findFilterMatchByFilterLabel(
      availableFilters,
      queryKey
    );
    if (!filterMatch) continue;

    const matchedValues = queryValue
      .split(",")
      .map((val) =>
        filterMatch.values.find(
          (v) =>
            v.label.toLowerCase() ===
            decodeURIComponent(val.toLowerCase()).trim()
        )
      )
      .filter((v): v is FilterValue => v !== undefined);

    preservedFilters.push(...matchedValues);
  }

  return preservedFilters;
};

export const getAppliedSortFromQuery = (availableSorts: SortOption[]) => {
  if (availableSorts.length === 0) return undefined;

  const search = typeof window !== "undefined" ? window.location.search : "";
  const params = new URLSearchParams(search);
  const sortParam = params.get("sort");
  if (!sortParam) return undefined;

  return availableSorts.find(({ name }) => name === sortParam);
};

// Build only the filter params from state
const buildFilterParamEntries = (
  appliedFilters: FilterValue[],
  availableFilters: Filter[] | undefined
): Array<[string, string]> => {
  if (!availableFilters || appliedFilters.length === 0) return [];

  const grouped: Record<string, FilterValue[]> = {};
  for (const filterValue of appliedFilters) {
    const filterMatch = findFilterMatchByFilterValueObject(
      availableFilters,
      filterValue
    );
    if (!filterMatch) continue;

    const key = transformToParamKey(filterMatch.label);
    (grouped[key] ||= []).push(filterValue);
  }

  const entries: Array<[string, string]> = [];
  for (const [key, values] of Object.entries(grouped)) {
    const valueString =
      key === COLLECTION_PARAMS.PRICE
        ? generatePriceParamValueString(values)
        : generateParamValueString(values);
    if (valueString) entries.push([key, valueString]);
  }

  return entries;
};

// Build only the sort param from state
export const buildSortParam = (
  appliedSort: SortOption | undefined,
  availableSorts: SortOption[]
): string | undefined => {
  if (!appliedSort || availableSorts.length === 0) return undefined;
  const match = availableSorts.find(({ name }) => name === appliedSort.name);

  // Only add sort parameter if it's not the default/first sort option
  if (match && availableSorts.length > 0) {
    const isDefaultSort = match.name === availableSorts[0].name;
    if (isDefaultSort) {
      return;
    }
  }

  return match ? match.name : undefined;
};

// Compose a new search string from existing search + filter and sort params (pure)
export const buildSearchFromFilterSortState = ({
  appliedFilters,
  appliedSort,
  availableFilters,
  availableSorts,
  existingSearch,
}: {
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
  existingSearch: string;
}): string => {
  const params = new URLSearchParams(existingSearch);

  // Clear all filter/sort params
  for (const key of Array.from(params.keys())) {
    if (isCollectionParamKey(key)) {
      params.delete(key);
    }
  }

  // Set filter params
  const filterEntries = buildFilterParamEntries(
    appliedFilters,
    availableFilters
  );
  for (const [key, value] of filterEntries) {
    params.set(key, value);
  }

  // Set sort param
  const sortName = buildSortParam(appliedSort, availableSorts);
  if (sortName) {
    params.set("sort", sortName);
  }

  return params.toString();
};

export const updateQueryFromFilterSortState = ({
  appliedFilters,
  appliedSort,
  availableFilters,
  availableSorts,
}: {
  appliedFilters: FilterValue[];
  appliedSort: SortOption | undefined;
  availableFilters: Filter[] | undefined;
  availableSorts: SortOption[];
}) => {
  if (typeof window === "undefined") return;
  const existingSearch = window.location.search;

  const newSearch = buildSearchFromFilterSortState({
    appliedFilters,
    appliedSort,
    availableFilters,
    availableSorts,
    existingSearch,
  });

  const pathname = window.location.pathname;
  const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ""}`;
  if (window.history?.replaceState) {
    window.history.replaceState(null, "", newUrl);
  }
};

// For the queryfilter params during URL→state initialization
// Expected format: "<filterLabel>=<filterValueLabel>" where <filterLabel>
// corresponds to transformToParamKey(filter.label).
// Example: "collection_name=Char"
const findFilterMatchByFilterLabel = (
  availableFilters: Filter[],
  filterLabel: string
) => {
  return availableFilters.find(
    (f) => transformToParamKey(f.label) === filterLabel
  );
};

// Helper to find the parent filter from an applied FilterValue during
// state→query updates.
// Example FilterValue:
// { id: "filter.v.m.collection.name__Char", count: 0, input: "Char", label: "Char" }
const findFilterMatchByFilterValueObject = (
  availableFilters: Filter[],
  filterValue: FilterValue
) => {
  return availableFilters.find((f) =>
    f.values.find((v) => filterValue.id.includes(v.id))
  );
};

const generateParamValueString = (filterValues: FilterValue[]): string => {
  return filterValues.map((v) => v.label.toLowerCase()).join(",");
};

const generatePriceParamValueString = (filterValues: FilterValue[]): string => {
  const input = filterValues[0]?.input;
  if (!input || typeof input !== "string") return "";

  try {
    const { price } = JSON.parse(input);
    const { max, min } = price;
    if (typeof min === "number" && typeof max === "number" && min < max) {
      return `${min}-${max}`;
    }
  } catch {
    console.error("Invalid price query value");
  }
  return "";
};

export const createDefaultCollectionSortOptions = (labels: {
  bestsellersLabel: string;
  featuredLabel: string;
  newestLabel: string;
  priceHighToLowLabel: string;
  priceLowToHighLabel: string;
}): SortOption[] => [
  {
    value: { sortKey: "COLLECTION_DEFAULT", reverse: false },
    name: labels.featuredLabel,
  },
  {
    value: { sortKey: "CREATED", reverse: true },
    name: labels.newestLabel,
  },
  {
    value: { sortKey: "PRICE", reverse: false },
    name: labels.priceLowToHighLabel,
  },
  {
    value: { sortKey: "PRICE", reverse: true },
    name: labels.priceHighToLowLabel,
  },
  {
    value: { sortKey: "BEST_SELLING", reverse: false },
    name: labels.bestsellersLabel,
  },
];
